import { Component, Input, EventEmitter, Output, SimpleChanges, ChangeDetectionStrategy, ChangeDetectorRef, NgZone, OnDestroy } from "@angular/core";
import { Subscription } from "rxjs";
import { CdkDragDrop, moveItemInArray, transferArrayItem, CdkDrag, CdkDropList } from "@angular/cdk/drag-drop"; 

import { DynamicDialogService } from "../../../core/services/dynamic-dialog.service";
import { Dialog, DialogButton } from "../../../core/models/dialog.model";
import { DynamicComponentLoaderDirective } from "../../../common/directives/dynamic-component-loader.directive";

import { Button, ContextConfig, FlowTemplate, Page, PageType, Question, QuestionKind, Section, SectionConfig, State, SummaryControlConfig } from "../../../common/models/flow.dto";
import { genId } from "../../../core/services/genId";
import { deepClone } from "../../models/editor.model";
import { StateBuilderService } from "../../services/state-builder.service"; 

@Component({
    selector: "state-builder-page",
    templateUrl: "./state-builder-page.component.html",
    styleUrls: ["./state-builder-page.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class StateBuilderPageComponent implements OnDestroy {

  private _questionsChangedSubscription: Subscription | undefined;

	@Input() dropListId: string;
	@Input() connectedDropLists: string[];
	@Output() itemDropped = new EventEmitter<CdkDragDrop<any>>();
  @Input() public showPanel: boolean;
  @Input() set activeTemplate(value: FlowTemplate) { //connecting the overall template; idea is that any edits to the selected state ought to be made to the overall template, as its ultimately all going to get saved as a whole
    this._activeTemplate = value;
    this._changeDetectorRef.detectChanges();
  }
  @Input() public selectedState: State;
  @Input() set currentPage(value) {
	  this._currentPage = value;
	  this._currentPage.questions.forEach(group => {
		  if (!Object.prototype.hasOwnProperty.call(group, '_rowId')) {
			  Object.defineProperty(group, '_rowId', {
				  value: genId(),
				  writable: false,
				  configurable: true,
				  enumerable: false
			  })
		  }
	  });
    if (this._currentPage && this.selectedState)
      this._currentSection = this.selectedState.sections.find(s => s.id === this._currentPage.section);
    else
      this._currentSection = { id: "", name: "" } as Section;
    this.updateServiceRowIds();
  }
  @Output() public openSettingsEvent: EventEmitter<DynamicComponentLoaderDirective> = new EventEmitter<DynamicComponentLoaderDirective>();
  @Output() public showRulesEvent: EventEmitter<Button> = new EventEmitter<Button>();
  @Output() public selectedQuestionEvent: EventEmitter<Question> = new EventEmitter<Question>();

  public static DRAG_DROP_FORMAT_TAG: string = "text";
  public pageType = PageType;
  public _activeTemplate: FlowTemplate;
  public _currentSection: Section;
  public _flexGrowFraction: number = .2;
  public _flowGrowMinFractionSize: number = .2;
  public _selectedState: State;
  public readonly deleteText: string = "Are you sure you want to delete this ##?";
  public readonly rowWarning: string = "(Deleting the last question from a row will cause the row to be deleted.)";
  public currentlyEditingQuestion: Question;
  public currentlyEditingQuestionHasType: boolean = false;
  public isEditingTitle: boolean = false;
  public pageTitlePlaceholder: string = 'Enter Page Title';
  public _currentPage: Page;
  public pageNameNonUnique: boolean = false;
  public question: Question;
  public activeQuestionGroup: number;
  public questionGroup: Array<any>;
  public questionIndex: number;
  public rowIndex: number;
  public page: Page;

  get currentSection(): Section {
    return this._currentSection;
  }

  get currentPage() {
    return this._currentPage;
	}

  get showRowArea(): boolean {
    if (this.currentPage === undefined || this.currentPage === null) return false;
    return (this.currentPage.pageType === PageType.Input || this.currentPage.pageType === PageType.Summary);
    }

    deleteQuestionDialog: Dialog = new Dialog(this.deleteText, [new DialogButton("Yes", true, () => this.onDialogConfirm('deleteQuestion')), new DialogButton("No", false, () => this.onDialogClose())]);
    deleteGroupDialog: Dialog = new Dialog(this.deleteText, [new DialogButton("Yes", true, () => this.onDialogConfirm('deleteQuestionGroup')), new DialogButton("No", false, () => this.onDialogClose())]);

  constructor(
      public _changeDetectorRef: ChangeDetectorRef,
      public stateBuilderService: StateBuilderService,
      public dialogService: DynamicDialogService
  ) {
    //this.setupDragula();
  }

    ngOnInit(): void {
        this.dialogService.updateDialog(this.deleteQuestionDialog);
    if (this.currentPage) {
      if (this.currentPage.pageType === PageType.Summary)
        this.updateSummaryQuestions();
    }
    this._questionsChangedSubscription = this.stateBuilderService.questionsChanged$.subscribe(() => {
      this._changeDetectorRef.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this._questionsChangedSubscription?.unsubscribe();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes["currentPage"]) {
      this.ensureButtonIds();
      if (this.currentPage.pageType === PageType.Summary)
        this.updateSummaryQuestions();
    }
  }

  public ensureButtonIds(): void {
    for (let b = 0; b < this.currentPage.buttons.length; b++) {
      if (this.currentPage.buttons[b].id === undefined || this.currentPage.buttons[b].id === null) {
        this.currentPage.buttons[b].id = genId();
      }
    }
  }

  get activeTemplate(): FlowTemplate {
    return this._activeTemplate;
  }

  get hasDesignRows() {
    if (!this.currentPage || !this.currentPage.questions) {
      return false;
    } else {
      return this.currentPage.questions.length > 0;
    }
  }

  public updateSummaryQuestions(): void {
    let summaryQuestion: Question;
    for (let r = 0; r < this.currentPage.questions.length; r++) {
      const row = this.currentPage.questions[r];
      summaryQuestion = row.find(q => q.type === QuestionKind.Summary);
      if (summaryQuestion !== undefined)
        break;
    }
    if (summaryQuestion === undefined || summaryQuestion === null)
      return;
    if (summaryQuestion.config === undefined || summaryQuestion.config === null) {
      const sections = new Array<Section>();
      const context = new Array<ContextConfig>();
      const sectionConfigs = new Array<SectionConfig>();
      summaryQuestion.config = { sections: sections, contextValues: context, configurations: sectionConfigs } as SummaryControlConfig;
    }

    const state = this.selectedState;
    state.sections.forEach(s => {
      if (!summaryQuestion.config.sections)
        summaryQuestion.config.sections = new Array<Section>();
      const section = deepClone(s) as Section;
      let existingSection = summaryQuestion.config.sections.find(st => st.id === section.id);
      if (!existingSection)
        summaryQuestion.config.sections.push(section);
      s.pages.forEach(page => {
        if (page.pageType !== PageType.Input && page.pageType !== PageType.NewCasingPlan) {
          section.pages = section.pages.filter(p => p.pageNumber !== page.pageNumber);
        } else {
          if (summaryQuestion.config.pages)
            summaryQuestion.config.pages = undefined;
          page.questions.forEach(row => {
            row.forEach(question => {
              if (question.changeRules) {
                question.changeRules.forEach(rule => {
                  const ruleParts = rule.contextTarget.split(".");
                  if (ruleParts[ruleParts.length - 1] === "value") { //only want those change target rules that are targeting a "value" node in the context
                    const contextConfig = {
                      startPageNumber: page.pageNumber,
                      sectionName: page.section,
                      sectionId: section.id,
                      contextTarget: rule.contextTarget
                    };
                    summaryQuestion.config.contextValues.push(contextConfig);
                  }
                })
              }
            })
          });
        }
      });
    });

    this._changeDetectorRef.detectChanges();
  }

  public deleteQuestion(questionGroup: Array<any>, questionIndex: number, rowIndex: number): void {
    this.questionGroup = questionGroup;
    this.questionIndex = questionIndex;
    this.rowIndex = rowIndex;
    let warning = this.deleteText.replace("##", "question");
    if (questionGroup.length === 1) {
      warning = warning + "<br /> " + this.rowWarning;
      }
      this.deleteQuestionDialog.message = warning;
      this.deleteQuestionDialog.toggleDialog(true);
      this.dialogService.updateDialog(this.deleteQuestionDialog);
    this._changeDetectorRef.detectChanges();
  }

  public deleteQuestionGroup(e: MouseEvent, page: Page, questionIndex: number): void {
    //block 'Enter' button from promting and deleteing row
    this.page = page;
    this.questionIndex = questionIndex;
    if (e.screenX != 0) {
        const warning = this.deleteText.replace("##", "row");
        this.deleteGroupDialog.message = warning;
        this.deleteGroupDialog.toggleDialog(true);
        this.dialogService.updateDialog(this.deleteGroupDialog);
      this._changeDetectorRef.detectChanges();
    }
  }

  //shows smallest question width limitation in UI
  public disabledQuestionSize(question: Question): boolean {
    if (question.flexGrow === undefined) { return false }
    //sometimes flexgrow value needs to be rounded to first decimal (ex. .600000000001)
    return Math.round(question.flexGrow * 10) / 10 == this._flowGrowMinFractionSize;
  }

  public flexGrow(question: Question, increase: boolean): void {
    //TODO: Might not need this after flexGrow is Saved and Retrieved from the DB
    if (question.flexGrow == undefined || question.flexGrow == 0) { question.flexGrow = 1 }

    if (increase) {
      question.flexGrow += this._flexGrowFraction;
    } else {
      //limit smallest size
      if (question.flexGrow - this._flexGrowFraction >= this._flowGrowMinFractionSize) {
        question.flexGrow -= this._flexGrowFraction;
      }
    }
    this._changeDetectorRef.detectChanges();
  }

  public isUniquePageName(pageName): boolean {
    //TODO: make sure >= 1 works when loading previously saved form page data
    let matches: number = 0;
    for (let i = 0; i < this.selectedState.sections.length; i++) {
      const section = this.selectedState.sections[i];
      for (let p = 0; p < section.pages.length; p++) {
        const displayName = section.pages[p].subtitle;
        if (displayName === pageName) {
          matches += 1;
        }
      }
    }
    //depending on OnInit, txtbx value could be "", therefore <= 1
    return matches <= 1;
  }

  public modifyQuestion(e: Event, question: Question, componentRef: DynamicComponentLoaderDirective): void {
    //don't bubble to parent div
    e.stopPropagation();
    //open the hidden menu
    this.openSettingsEvent.emit(componentRef);
	}

	rowSize: number = 1;

	addRow(): void {
		let row: Question[] = [];
		for (let i = 0; i < this.rowSize; i++) {
			let question = {
				type: "empty",
				key: genId(),
				config: { domain: undefined }
			} as Question;
			row.push(question);
		}
		this.assignRowId(row);
		this._currentPage.questions.push(row);
		this.updateServiceRowIds();
	}


	canDropRow = (drag: CdkDrag, drop: CdkDropList): boolean => {
		// Allow row reordering: source is the outer page container
		if (drag.dropContainer?.id === 'pageRowDropList') return true;
		// Allow only row palette items; component items go to inner row lists via canDropQuestion
		const dragData = this.stateBuilderService.getDragData();
		return dragData?.type === 'row';
	};

	canDropQuestion = (drag: CdkDrag, drop: CdkDropList): boolean => {
		const dragData = this.stateBuilderService.getDragData();
		// Allow component palette items
		if (dragData != null) return dragData.type === 'component';
		// Allow internal drags from row question lists; reject row-level drags (source is outer container)
		return drag.dropContainer?.id !== 'pageRowDropList';
	};

	private updateServiceRowIds(): void {
		const ids = (this._currentPage?.questions || [])
			.map(group => `rowDropList-${(group as any)._rowId}`)
			.filter(id => !id.endsWith('undefined'));
		this.stateBuilderService.setInnerRowDropListIds(ids);
	}

	private assignRowId(row: Question[]): void {
		Object.defineProperty(row, '_rowId', {
			value: genId(),
			writable: false,
			configurable: true,
			enumerable: false
		});
	}

	private createQuestionFromDragData(dragData: any, question?: Question): Question {
		let value = (dragData.type === 'component')
			? dragData.value
			: 'empty';
		let newQuestion = {
			type: value,
			key: genId(),
			config: question?.config || { domain: undefined }

		} as Question;
		return newQuestion;
	}

	onQuestionDrop(event: CdkDragDrop<any[]>, rowIndex: number) {
		const dragData = this.stateBuilderService.getDragData();
		if (event.previousContainer === event.container && dragData == null) {
			// Reorder within the same row
			moveItemInArray(this._currentPage.questions[rowIndex], event.previousIndex, event.currentIndex);
		} else if (event.previousContainer.id.startsWith('rowDropList-')) {
			// Move between rows
			const prevRowIndex = this._currentPage.questions.findIndex(group => group === event.previousContainer.data);
			transferArrayItem(
				this._currentPage.questions[prevRowIndex],
				this._currentPage.questions[rowIndex],
				event.previousIndex,
				event.currentIndex
			);
		} else if (dragData != null) {
			// Drop from DnD palette into an existing row
			const row = this._currentPage.questions[rowIndex];
			if (row) {
				const emptyIndex = row.findIndex(q => q.type === 'empty');
				if (emptyIndex >= 0) {
					row[emptyIndex] = this.createQuestionFromDragData(dragData);
				} else if (row.length < 3) {
					row.push(this.createQuestionFromDragData(dragData));
				}
				// If row already has 3 non-empty questions, do nothing
			}
			this.stateBuilderService.clearDragData();
		}
		this._changeDetectorRef.detectChanges();
		this.stateBuilderService.questionsChanged$.next();
	}

	public onRowDrop(event: CdkDragDrop<any[]>): void {
		const dragData = this.stateBuilderService.getDragData();

		// If the source container is the same as the target, it's a reorder
		if (event.previousContainer === event.container && dragData == null) {
			moveItemInArray(this._currentPage.questions, event.previousIndex, event.currentIndex);
		} else if (dragData != null) {
			// Create a new question group (row) from dragData
			let newRow: Question[];
			if (dragData.type === 'row') {
				newRow = Array.from({ length: dragData.count || 1 }, () => this.createQuestionFromDragData(dragData));
			} else {
				newRow = [this.createQuestionFromDragData(dragData)];
			}
			this.assignRowId(newRow);
			// Insert at the drop position
			this._currentPage.questions.splice(event.currentIndex, 0, newRow);
			this.stateBuilderService.clearDragData();
			this.updateServiceRowIds();
		}
		this._changeDetectorRef.detectChanges();
    this.stateBuilderService.questionsChanged$.next();
	}

	getConnectedRowListIds(currentRowId: string): string[] {
		return this._currentPage?.questions
			?.filter(group => (group as any)._rowId !== currentRowId)
			.map(group => `rowDropList-${(group as any)._rowId}`) || [];
	}

  // show cell icons "drag, +/-, cog"
  public questionCellClick(question: Question | null): void {
    if (question != null) {
      this.currentlyEditingQuestion = question;
      this.selectedQuestionEvent.emit(question);
      //update var for hide/show "+" and cog
      this.currentlyEditingQuestionHasType = question.type != undefined;
    } else {
      this.currentlyEditingQuestionHasType = false;
    }
    this._changeDetectorRef.detectChanges();
  }

  public questionCellDrop(e: any, question: Question) {
    question.type = e.dragData.value;
    question.label = `Label for ${question.type}`;
    this._changeDetectorRef.detectChanges();
  }

  public bubbleShowRules(button: Button): void {
    this.showRulesEvent.emit(button);
  }

  public isEditableQuestionType(question: Question): boolean {
    const types = [QuestionKind.Summary];
    return !types.includes(question.type);
  }

    public onDialogConfirm(dialogType: string): void {
    if (dialogType === 'deleteQuestion') {
      this.questionGroup.splice(this.questionIndex, 1);
      if (this.questionGroup.length === 0) {
        this.currentPage.questions.splice(this.rowIndex, 1);
        this._changeDetectorRef.detectChanges();
      }
    } else if (dialogType === 'deleteQuestionGroup') {
      this.page.questions.splice(this.questionIndex, 1);
      this._changeDetectorRef.detectChanges();
    }
  }

  public onDialogClose(): void {
  }

}
